import { I18N } from "aurelia-i18n";
import { DataType, SourceType, QueryFilter } from "../../../../../models/queries/queries.models";
import {autoinject, transient, observable, BindingEngine, useView} from "aurelia-framework";
import { BaseListItems } from "../base-list-items";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { ListService } from "../../../../../services/modules/list-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { ActiveMenuItemsService } from "../../../../../services/basics/active-menu-items.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { Guid } from "../../../../../utilities/guid";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";

@autoinject()
@transient()
@useView('views/pages/admin/lists/company-list-items/company-list-items.html')
export class CompanyListItems extends BaseListItems {

    listChanged() {

        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                name: 'fullName',
                title: 'Name',
                value: '<div class="column"><a class="link" href.bind="getCompanyUrl(item.companyId)">${item.fullName}</a>'
                    + '<span if.bind="item.owCode">IMO ${item.owCode}</span>'
                    + '</div>',
                parameter: {
                    item: null,
                    getCompanyUrl: this.getCompanyUrl.bind(this)
                },
                instancePerRow: true
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                isSortable: true,
                name: 'country',
                title: 'Country'
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                isSortable: false,
                name: 'comments',
                title: 'Comments',
                ellipsis: 100,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "200px",
            },
            {
                dataType: DataType.CustomTemplate,
                value:
                    `<div class="actions row justify-content-end align-items-middle">
                        <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)">
                            <template replace-part="menu-item">
                                <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                    <span class="text">\${menuItem.item.text}</span>
                                </div>  
                            </template>
                        </list-menu>
                    </div>`,
                name: "menu",
                title: "",
                instancePerRow: true,
                sortOrder: 10,
                columnOrder: 10,
                isSortable: false,
                parameter: {
                    item: null,
                    isRightShipUser: this.isRightShipUser,
                    menuItems: (item) => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.CompanyListItemDialog, {
                                        onComplete: () => {
                                            this.dataGridVM.reloadData(null, null);
                                        },
                                        hideActions: true,
                                        id: item ? item.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "List item details" },
                                classList: ''
                            },
                            {
                                isVisible: (this.canEditListItems && (this.isRightShipUser ||
                                    !this.businessUnitId || item.businessUnitId === this.businessUnitId ||
                                    !this.customerId || item.customerId === this.customerId) &&
                                    (!this.list.isInternal || this.canEditProtectedListItem)),
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.CompanyListItemDialog, {
                                        onComplete: () => {
                                            this.dataGridVM.reloadData(null, null);
                                        },
                                        isEdit: true,
                                        id: item ? item.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Edit" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        this.columns = columns;
        this.reload();
    }

    getCompanyUrl(companyId: number) {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.Company, {
            id: companyId,
        });
    }

    reload() {
        if (!this.list) return;

        const filters = [QueryFilter.getQuery('listId', this.list.listId, 'eq', DataType.Number)];

        if (this.searchFilter) {
            const searchGuid = Guid.generateGuid();
            filters.push(QueryFilter.getQuery("fullName", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, searchGuid, null, true));
            filters.push(QueryFilter.getQuery("owCode", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, true, searchGuid, null, true));
        }

        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/ListCompanies",
            columns: this.columns,
            initialFilters: filters
        };
    }

    constructor(private router: Router,
        dialogClient: DialogClientService,
        notificationClientService: NotificationClientService,
        userClaimsService: UserClaimsService,
        listService: ListService,
        i18N: I18N,
        activeMenuItemsService: ActiveMenuItemsService,
        eventAggregator: EventAggregator,
        customerService: CommonCustomerService,
        signalService: SignalClientService,
        validationsClientService: ValidationsClientService, protected bindingEngine: BindingEngine) {
        super(dialogClient, notificationClientService, listService, i18N, bindingEngine, 'Companies',
            activeMenuItemsService, eventAggregator, customerService, userClaimsService, signalService , validationsClientService);
        this.addSelectColumn = true;
    }

    itemClick(event) {
        if (!event.detail.item || event.detail.fromCheckbox) {
            return;
        }
        //since on click the page will be opened, there is no need to set isSelected to true
        if (event && event.detail && event.detail.row)
            event.detail.row.isSelected = false;

        const selectedItem = event.detail.item;

        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Company, { id: selectedItem.companyId });
    }

    //#region Search Filter
    @observable()
    public searchFilter: string = null;

    searchFilterChanged() {
        this.reload();
    }

    searchKeyDown($event) {
        if ($event && $event.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.reload()
            }
        }
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }
    //#endregion
}